import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  Search,
  SortAsc,
  RefreshCw,
  Briefcase,
  Users,
  Plus,
} from 'lucide-react'
import { jobsApi, applicationsApi } from '@/lib/api'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CandidateDrawer } from '@/components/features/candidates/CandidateDrawer'
import { PageLayout } from '@/components/layout/PageLayout'
import { toast } from '@/components/ui/toaster'
import { getInitials, formatRelativeTime } from '@/lib/utils'

interface Application {
  id: string
  stageId: string
  currentStageId?: string
  currentStage?: {
    id: string
    name: string
    color: string
    type?: string
  }
  stageName?: string
  stageColor?: string
  status: string
  candidate: {
    id: string
    firstName: string
    lastName: string
    name?: string
    email: string
    avatarUrl?: string
    rating?: number
  }
  job: {
    id: string
    title: string
  }
  appliedAt?: string
  createdAt: string
  source?: string
}

interface Stage {
  id: string
  name: string
  color: string
  order: number
  type?: string
}

interface Job {
  id: string
  title: string
  status: string
  applicationCount?: number
}

// Default pipeline stages for grouping
const DEFAULT_STAGES: Stage[] = [
  { id: 'applied', name: 'Applied', color: '#6b7280', order: 0, type: 'applied' },
  { id: 'screening', name: 'Screening', color: '#3b82f6', order: 1, type: 'screening' },
  { id: 'interview', name: 'Interview', color: '#8b5cf6', order: 2, type: 'interview' },
  { id: 'offer', name: 'Offer', color: '#f59e0b', order: 3, type: 'offer' },
  { id: 'hired', name: 'Hired', color: '#10b981', order: 4, type: 'hired' },
]

export function ApplicationsPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'rating'>('date')
  const [selectedJob, setSelectedJob] = useState<string | 'all'>('all')
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const queryClient = useQueryClient()

  // Fetch all jobs for filter dropdown
  const { data: jobsData } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => jobsApi.list(),
  })

  // Fetch all applications
  const { data: applicationsData, isLoading, refetch } = useQuery({
    queryKey: ['applications', { jobId: selectedJob === 'all' ? undefined : selectedJob }],
    queryFn: () => applicationsApi.list(selectedJob === 'all' ? undefined : { jobId: selectedJob }),
  })

  const jobs = ((jobsData as any)?.items || []) as Job[]
  const applications = (applicationsData as Application[]) || []

  // Move application mutation
  const moveApplication = useMutation({
    mutationFn: ({ applicationId, stageId }: { applicationId: string; stageId: string }) =>
      applicationsApi.move(applicationId, stageId),
    onSuccess: () => {
      toast({ title: 'Application moved', variant: 'success' })
      queryClient.invalidateQueries({ queryKey: ['applications'] })
    },
    onError: () => {
      toast({ title: 'Failed to move application', variant: 'destructive' })
    },
  })

  // Filter and sort applications
  const filteredApplications = useMemo(() => {
    let filtered = applications.filter((app) => {
      if (!searchQuery) return true
      const query = searchQuery.toLowerCase()
      const candidateName = app.candidate.name ||
        `${app.candidate.firstName} ${app.candidate.lastName}`
      return (
        candidateName.toLowerCase().includes(query) ||
        app.candidate.email.toLowerCase().includes(query) ||
        app.job.title.toLowerCase().includes(query)
      )
    })

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          const nameA = a.candidate.name || `${a.candidate.firstName} ${a.candidate.lastName}`
          const nameB = b.candidate.name || `${b.candidate.firstName} ${b.candidate.lastName}`
          return nameA.localeCompare(nameB)
        case 'rating':
          return (b.candidate.rating || 0) - (a.candidate.rating || 0)
        case 'date':
        default:
          const rawA = a.appliedAt || a.createdAt
          const rawB = b.appliedAt || b.createdAt
          const timeA = typeof rawA === 'number' ? rawA : new Date(rawA).getTime() / 1000
          const timeB = typeof rawB === 'number' ? rawB : new Date(rawB).getTime() / 1000
          return timeB - timeA
      }
    })
  }, [applications, searchQuery, sortBy])

  // Group applications by stage
  const applicationsByStage = useMemo(() => {
    const grouped: Record<string, Application[]> = {}
    DEFAULT_STAGES.forEach((stage) => {
      grouped[stage.id] = []
    })

    filteredApplications.forEach((app) => {
      // Map application stage to default stages based on currentStage type/name
      const stageType = (app.currentStage?.type || app.currentStage?.name || app.stageName || 'applied').toLowerCase()
      let targetStage = 'applied'

      if (stageType.includes('screen') || stageType.includes('review')) {
        targetStage = 'screening'
      } else if (stageType.includes('interview')) {
        targetStage = 'interview'
      } else if (stageType.includes('offer')) {
        targetStage = 'offer'
      } else if (stageType.includes('hired') || stageType.includes('accepted')) {
        targetStage = 'hired'
      } else if (stageType.includes('rejected')) {
        // Don't show rejected in kanban
        return
      }

      if (grouped[targetStage]) {
        grouped[targetStage].push(app)
      } else {
        grouped['applied'].push(app)
      }
    })

    return grouped
  }, [filteredApplications])

  const handleApplicationClick = (application: Application) => {
    setSelectedApplication(application)
    setIsDrawerOpen(true)
  }

  const handleDrop = (applicationId: string, stageId: string) => {
    moveApplication.mutate({ applicationId, stageId })
  }

  const handleDragStart = (e: React.DragEvent, applicationId: string) => {
    e.dataTransfer.setData('applicationId', applicationId)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDropOnColumn = (e: React.DragEvent, stageId: string) => {
    e.preventDefault()
    const applicationId = e.dataTransfer.getData('applicationId')
    if (applicationId) {
      handleDrop(applicationId, stageId)
    }
  }

  const toolbar = (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search candidates or jobs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 h-8"
        />
      </div>

      <div className="flex items-center gap-2">
        {/* Job Filter */}
        <Select value={selectedJob} onValueChange={setSelectedJob}>
          <SelectTrigger className="w-[180px] h-8 text-xs">
            <Briefcase className="h-3.5 w-3.5 mr-1" />
            <SelectValue placeholder="All Jobs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Jobs</SelectItem>
            {jobs.map((job) => (
              <SelectItem key={job.id} value={job.id}>
                {job.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sort */}
        <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
          <SelectTrigger className="w-[140px] h-8 text-xs">
            <SortAsc className="h-3.5 w-3.5 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date">Date Applied</SelectItem>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="rating">Rating</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" size="icon" aria-label="Refresh" onClick={() => refetch()}>
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>

        <Badge variant="secondary" className="text-xs">
          {filteredApplications.length}
        </Badge>

        <Button size="sm" asChild>
          <Link to="/applications/new">
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Link>
        </Button>
      </div>
    </div>
  )

  return (
    <PageLayout fullHeight toolbar={toolbar}>
      {/* Kanban Board */}
      <div className="h-full">
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto h-full pb-2">
          {DEFAULT_STAGES.map((stage) => (
            <div
              key={stage.id}
              className="flex-shrink-0 w-72 flex flex-col h-full"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDropOnColumn(e, stage.id)}
            >
              {/* Column Header */}
              <div
                className="flex items-center justify-between mb-2 px-2 flex-shrink-0"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: stage.color }}
                  />
                  <span className="font-medium">{stage.name}</span>
                  <Badge variant="secondary" className="text-xs">
                    {applicationsByStage[stage.id]?.length || 0}
                  </Badge>
                </div>
              </div>

              {/* Column Content */}
              <div className="space-y-2 flex-1 min-h-0 overflow-y-auto p-2 bg-muted/30 rounded-lg border-2 border-dashed border-transparent hover:border-muted-foreground/20 transition-colors">
                {applicationsByStage[stage.id]?.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full min-h-[120px] text-center text-muted-foreground">
                    <Users className="h-8 w-8 mb-2 opacity-30" />
                    <p className="text-xs">No candidates</p>
                    <p className="text-[10px] opacity-70">Drag candidates here</p>
                  </div>
                )}
                {applicationsByStage[stage.id]?.map((application) => {
                  const candidateName = application.candidate.name ||
                    `${application.candidate.firstName} ${application.candidate.lastName}`

                  return (
                    <motion.div
                      key={application.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.15 }}
                      draggable
                      onDragStart={(e: any) => handleDragStart(e, application.id)}
                      onClick={() => handleApplicationClick(application)}
                      className="cursor-pointer"
                    >
                      <Card className="hover:shadow-md transition-shadow">
                        <CardContent className="p-3">
                          <div className="flex items-start gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={application.candidate.avatarUrl} />
                              <AvatarFallback className="text-xs">
                                {getInitials(candidateName)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">
                                {candidateName}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {application.job.title}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-xs text-muted-foreground">
                                  {formatRelativeTime(
                                  typeof (application.appliedAt || application.createdAt) === 'number'
                                    ? (application.appliedAt || application.createdAt) as unknown as number
                                    : new Date(application.appliedAt || application.createdAt).getTime() / 1000
                                )}
                                </span>
                                {application.source && (
                                  <Badge variant="outline" className="text-[10px] px-1 truncate max-w-[80px]">
                                    {application.source}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Candidate Drawer */}
      {selectedApplication && (
        <CandidateDrawer
          open={isDrawerOpen}
          onOpenChange={setIsDrawerOpen}
          candidateId={selectedApplication.candidate.id}
          applicationId={selectedApplication.id}
        />
      )}
    </PageLayout>
  )
}
